import { PlusOutlined } from '@ant-design/icons';
import { useCustom, useGetLocale, useTranslate } from '@refinedev/core';
import { Alert, Button, Card, Input, Space, Table, Typography } from 'antd';
import { useState } from 'react';
import { CardEditor } from '../diaryCard/CardEditor';
import {
  TEMPLATE_FIELDS,
  blankDraft,
  draftFromTemplate,
  draftProblem,
  publishInputOf,
  type CardDraft,
  type TemplateRow,
} from '../diaryCard/model';
import { useDiaryCardCommand } from '../diaryCard/useDiaryCardCommand';

/**
 * E37 · PublishDiaryCardTemplate (buildOrder 10) — "Backoffice — Diary card templates — Publish" (cc-assessments, chapter
 * "Check-in — Diary card (portal + mobile, E37)", screen e37e0010-0001-4000-8000-000000000020): the catalogue the phone's
 * "Set up my diary card" offers (dbtStandard, dbtSkills, eatingDay, drinkingDay are seeded; this is how a fifth arrives
 * without a release). A known slug revises it — the latest publication wins. Catalogue data, no person. Members only (the
 * server's gate). The row labels are edited in the i18n catalogue, not here.
 */
export const BackofficeDiaryCardTemplates = () => {
  const getLocale = useGetLocale();
  const locale: 'en' | 'ru' = getLocale()?.startsWith('ru') ? 'ru' : 'en';
  const t = useTranslate() as unknown as (k: string, o?: Record<string, unknown>) => string;
  const { run, isLoading } = useDiaryCardCommand();
  const { data, isFetching, isError, refetch } = useCustom<TemplateRow[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'diaryCardTemplates',
      fields: TEMPLATE_FIELDS,
      variables: { locale: { name: 'locale', type: 'String', value: locale } },
    },
  });
  const [open, setOpen] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [name, setName] = useState('');
  const [summary, setSummary] = useState('');
  const [draft, setDraft] = useState<CardDraft>(blankDraft());
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const edit = (row: TemplateRow | null) => {
    setMessage(null);
    setTemplateId(row?.templateId ?? '');
    setName(row?.name ?? '');
    setSummary(row?.summary ?? '');
    setDraft(row ? draftFromTemplate(row) : blankDraft());
    setOpen(true);
  };

  const problem = draftProblem(draft);
  const ready = !problem && /^[A-Za-z][A-Za-z0-9_-]{1,48}$/.test(templateId.trim()) && name.trim() && summary.trim();

  const publish = async () => {
    const res = await run(
      'publishDiaryCardTemplate',
      publishInputOf(templateId, name, summary, draft),
      t('backoffice.diaryCardTemplates.failed'),
    );
    if (res.ok) {
      setMessage({ type: 'success', text: t('backoffice.diaryCardTemplates.published') });
      setOpen(false);
      refetch();
    } else {
      setMessage({ type: 'error', text: res.message });
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }} data-testid="backoffice-diary-templates">
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <div>
          <Typography.Title level={3} style={{ marginBottom: 0 }}>
            {t('backoffice.diaryCardTemplates.title')}
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('backoffice.diaryCardTemplates.intro')}
          </Typography.Paragraph>
        </div>
        <Button
          data-testid="backoffice-diary-new"
          icon={<PlusOutlined />}
          onClick={() => edit(null)}
          type="primary"
        >
          {t('backoffice.diaryCardTemplates.new')}
        </Button>
      </Space>
      {message ? (
        <Alert type={message.type} showIcon message={message.text} data-testid={`backoffice-diary-${message.type}`} />
      ) : null}
      {isError ? <Alert type="error" showIcon message={t('backoffice.diaryCardTemplates.loadFailed')} /> : null}
      {open ? (
        <Card title={t('backoffice.diaryCardTemplates.formTitle')} data-testid="backoffice-diary-form">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Input
              data-testid="backoffice-diary-id"
              onChange={(e) => setTemplateId(e.target.value)}
              placeholder={t('backoffice.diaryCardTemplates.templateId')}
              value={templateId}
            />
            <Input
              data-testid="backoffice-diary-name"
              onChange={(e) => setName(e.target.value)}
              placeholder={t('backoffice.diaryCardTemplates.name')}
              value={name}
            />
            <Input
              data-testid="backoffice-diary-summary"
              onChange={(e) => setSummary(e.target.value)}
              placeholder={t('backoffice.diaryCardTemplates.summary')}
              value={summary}
            />
            <Typography.Text type="secondary">{t('backoffice.diaryCardTemplates.labelsHint')}</Typography.Text>
            <CardEditor draft={draft} idPrefix="backoffice-diary-editor" locale={locale} onChange={setDraft} />
            <Space>
              <Button
                data-testid="backoffice-diary-publish"
                disabled={!ready}
                loading={isLoading}
                onClick={publish}
                type="primary"
              >
                {t('backoffice.diaryCardTemplates.publish')}
              </Button>
              <Button onClick={() => setOpen(false)}>{t('backoffice.diaryCardTemplates.cancel')}</Button>
            </Space>
          </Space>
        </Card>
      ) : null}
      <Table<TemplateRow>
        rowKey="templateId"
        loading={isFetching}
        dataSource={data?.data ?? []}
        pagination={false}
        locale={{ emptyText: t('backoffice.diaryCardTemplates.empty') }}
        onRow={(r) => ({ 'data-testid': `backoffice-diary-row-${r.templateId}` } as never)}
        columns={[
          { title: t('backoffice.diaryCardTemplates.name'), dataIndex: 'name' },
          { title: 'ID', dataIndex: 'templateId' },
          { title: t('backoffice.diaryCardTemplates.rows'), render: (_: unknown, r) => r.targets.length },
          {
            title: '',
            key: 'actions',
            render: (_: unknown, r) => (
              <Button data-testid={`backoffice-diary-edit-${r.templateId}`} onClick={() => edit(r)}>
                {t('backoffice.diaryCardTemplates.revise')}
              </Button>
            ),
          },
        ]}
      />
    </Space>
  );
};
